<p-dialog [(visible)]="visible" [modal]="true" [draggable]="false" [resizable]="false"
  [style]="{ width: 'var(--modal-width-lg)', maxWidth: '95vw' }"
  [contentStyle]="{ maxHeight: '85vh', overflow: 'hidden', padding: '0' }"
  [showHeader]="false" appendTo="body" styleClass="rounded-2xl overflow-hidden shadow-2xl transition-all duration-300" (onHide)="close()">

  <div class="import-dialog-body font-sans flex flex-col h-full max-h-[85vh]">

    <!-- SECTION A: DIALOG HEADER -->
    <div class="import-dialog-header shrink-0">
      <h3 class="text-lg font-bold text-text-main leading-tight">
        Bulk Import {{ importConfig().entityName }}s
      </h3>
      <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" (onClick)="close()"></p-button>
    </div>

    <!-- SECTION B: MAIN CONTENT (SCROLLABLE) -->
    <div class="overflow-y-auto flex-1 p-6 pt-0">
      <div class="w-full flex flex-col gap-4">

        @if (!parseResult()?.valid) {
          <!-- 1. Template Download Section -->
          <div class="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl bg-surface-50/50 dark:bg-surface-800/10 border border-surface-100 dark:border-surface-800/50 gap-4">
            <div class="text-center sm:text-left">
              <h4 class="text-sm font-bold text-text-main">Download Template</h4>
              <p class="text-xs text-text-muted mt-0.5">Get the standard format to fill your data</p>
            </div>
            <p-button label="Download CSV" icon="pi pi-download" [outlined]="true" size="small" styleClass="w-full sm:w-auto" (onClick)="downloadTemplate()"></p-button>
          </div>

          <!-- 2. Select or Drop File View (Upload Area) -->
          <div class="flex flex-col gap-2">
            @if (!selectedFile()) {
              <!-- Drop Zone -->
              <div [class]="dropzoneClass()" (dragover)="onDragOver($event)" (dragleave)="onDragLeave()"
                (drop)="onDrop($event)" (click)="fileInput.click()"
                role="button" aria-label="Upload file — click or drag and drop" tabindex="0">
                <div class="w-12 h-12 rounded-2xl flex items-center justify-center mb-2 bg-primary-50 dark:bg-primary-900/20">
                  <i class="pi pi-cloud-upload text-2xl text-primary-400"></i>
                </div>
                <div class="text-center">
                  <p class="text-sm font-bold text-text-main">Select or drop file</p>
                  <p class="text-xs text-text-muted mt-1">Browse your computer to upload</p>
                </div>
                <input #fileInput type="file" accept=".csv,.xlsx" class="hidden" (change)="onFileSelected($event)" aria-label="File input" />
              </div>

              <!-- 3. Format & Size Info -->
              <p class="text-xs font-medium text-text-muted text-center">
                CSV or Excel format accepted &middot; Max {{ importConfig().maxFileSizeMb || 5 }}MB
              </p>
            } @else {
              <!-- File Info Card -->
              <div class="flex items-center gap-4 p-4 rounded-xl border border-surface-100 dark:border-surface-700/50 bg-surface-50/30 dark:bg-surface-800/20">
                <div class="w-10 h-10 rounded-lg shrink-0 flex items-center justify-center bg-primary-50 dark:bg-primary-900/20">
                  <i [class]="'pi ' + (selectedFile()?.name?.endsWith('.xlsx') ? 'pi-file-excel' : 'pi-file') + ' text-primary-500 text-lg'"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-bold text-text-main truncate">{{ selectedFile()!.name }}</p>
                  <p class="text-xs text-text-muted mt-0.5">{{ (selectedFile()!.size / 1024).toFixed(1) }}KB</p>
                </div>
                <p-button icon="pi pi-times" [rounded]="true" [text]="true" severity="secondary" size="small" (onClick)="clearFile()"></p-button>
              </div>

              <!-- Validation Flow (Errors Only) -->
              @if (isProcessing()) {
                <div class="flex flex-col items-center justify-center py-10 gap-3" aria-live="polite" aria-busy="true">
                  <i class="pi pi-spinner pi-spin text-primary-500 text-2xl"></i>
                  <p class="text-sm font-medium text-text-muted">Validating file with server...</p>
                </div>
              } @else if (parseResult() && !parseResult()!.valid) {
                <!-- Summary Card — uses semantic alert class -->
                <div class="validation-row-error" role="alert">
                  <i class="pi text-xl pi-exclamation-triangle text-danger-500"></i>
                  <div class="flex-1">
                    <p class="text-sm font-bold text-danger-700 dark:text-danger-300">
                      {{ parseResult()!.errorCount }} issues found in file
                    </p>
                    <p class="text-xs text-text-muted mt-0.5">{{ parseResult()!.totalRows }} rows checked</p>
                  </div>
                </div>

                <!-- Errors List -->
                <div class="rounded-xl border border-red-200 dark:border-red-800/40 overflow-hidden">
                  <table class="w-full text-xs" role="list" aria-label="Validation errors">
                    <thead>
                      <tr class="import-row-error">
                        <th class="text-left px-3 py-2 font-semibold text-red-700 dark:text-red-300 w-16">Row</th>
                        <th class="text-left px-3 py-2 font-semibold text-red-700 dark:text-red-300 w-32">Field</th>
                        <th class="text-left px-3 py-2 font-semibold text-red-700 dark:text-red-300">Issue</th>
                      </tr>
                    </thead>
                    <tbody class="max-h-52 overflow-y-auto">
                      @for (group of groupedErrors().slice(0, 20); track group.row) {
                        @for (line of group.lines; track line.label; let first = $first) {
                          <tr class="import-row-error-item">
                            <td class="px-3 py-2 text-red-600 dark:text-red-400 font-semibold align-top">
                              @if (first) { Row {{ group.row + 1 }} }
                            </td>
                            <td class="px-3 py-2 text-text-main font-medium align-top">{{ line.label }}</td>
                            <td class="px-3 py-2 text-text-muted align-top">{{ line.message }}</td>
                          </tr>
                        }
                      }
                    </tbody>
                  </table>
                  @if (groupedErrors().length > 20) {
                    <div class="px-3 py-2 import-error-summary text-center">
                      <span class="text-xs text-text-muted italic">+ {{ groupedErrors().length - 20 }} more rows with issues</span>
                    </div>
                  }
                </div>
              }
            }
          </div>

          <!-- 4. How to Import Section -->
          <div class="p-5 rounded-xl bg-surface-50/50 dark:bg-surface-800/10 border border-surface-100 dark:border-surface-800/50">
            <h4 class="label-micro mb-2 tracking-tight">How to import</h4>
            <ul class="flex flex-col gap-3" role="list">
              @for (step of [
                'Download the CSV template',
                'Fill in your records (one per row)',
                'Remove sample rows if any',
                'Upload and click Validate'
              ]; track $index) {
                <li class="flex items-center gap-3 text-xs text-text-muted">
                  <span class="import-progress-dot active w-5 h-5 text-xs">{{ $index + 1 }}</span>
                  {{ step }}
                </li>
              }
            </ul>
          </div>

        } @else {
          <!-- REVIEW GRID VIEW (Validation Passed) -->
          <div class="flex flex-col gap-4 h-full">
            <div class="validation-row-ok" role="status">
              <i class="pi text-xl pi-check-circle text-success-500"></i>
              <div class="flex-1">
                <p class="text-sm font-bold text-success-700 dark:text-success-300">
                  Validation Passed! {{ parseResult()!.validRows.length }} records ready for import.
                </p>
                <p class="text-xs text-success-600/80 dark:text-success-400/80 mt-0.5">Please review the parsed data in the grid below before confirming.</p>
              </div>
              <p-button icon="pi pi-refresh" label="Start Over" size="small" [outlined]="true" severity="secondary" (onClick)="clearFile()"></p-button>
            </div>

            @if (importConfig().batchTimezone) {
              <div class="flex items-center gap-3 p-3 rounded-xl border border-surface-100 dark:border-surface-700/50 bg-surface-50/30 dark:bg-surface-800/20">
                <i class="pi pi-globe text-primary-500 text-sm shrink-0"></i>
                <div class="flex-1 min-w-0">
                  <p class="text-xs font-semibold text-text-main leading-tight">Batch Timezone</p>
                  <p class="text-xs text-text-muted mt-0.5">Applied to records that don't have their own timezone column value.</p>
                </div>
                <p-select
                  [ngModel]="batchTimezone()"
                  (ngModelChange)="batchTimezone.set($event)"
                  [options]="orgTz.options()"
                  optionLabel="label"
                  optionValue="timezone"
                  placeholder="Select timezone"
                  [filter]="true"
                  filterBy="label,timezone"
                  styleClass="w-56 text-sm">
                </p-select>
              </div>
            }

            <div class="flex-1 min-h-[400px]">
              <app-import-review-grid
                [columns]="reviewColumns()"
                [data]="parseResult()!.displayRows ?? parseResult()!.validRows">
              </app-import-review-grid>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- SECTION C: FOOTER ACTIONS -->
    <div class="import-dialog-footer shrink-0 flex items-center justify-between py-3 px-4 lg:py-4 lg:px-6 border-t border-surface-100 dark:border-surface-700/50 bg-surface-50/30 dark:bg-surface-800/10">
      <div class="flex-1"></div>
      <div class="flex items-center gap-3">
        <p-button label="Cancel" [outlined]="true" severity="secondary" (onClick)="close()"></p-button>
        <p-button
          [label]="parseResult()?.valid ? 'Confirm & Import' : 'Validate File'"
          [disabled]="!selectedFile() || (parseResult() !== null && !parseResult()!.valid)"
          [loading]="isProcessing()"
          (onClick)="parseResult()?.valid ? onSubmit() : null">
        </p-button>
      </div>
    </div>

  </div>
</p-dialog>
